import * as React from 'react';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils';

export interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(({ className, label, error, hint, icon, onChange, onBlur, value, defaultValue, ...props }, ref) => {
  const innerRef = React.useRef<HTMLInputElement>(null);

  // merge refs
  const setRef = React.useCallback(
    (el: HTMLInputElement | null) => {
      (innerRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
    },
    [ref],
  );

  // Format số → string có dấu phẩy
  const fmt = (v: string | number | undefined | null): string => {
    if (v === undefined || v === null || v === '') return '';
    const digits = String(v).replace(/\D/g, '');
    if (!digits) return '';
    return formatNumber(Number(digits));
  };

  // Khởi tạo display từ value/defaultValue
  const [display, setDisplay] = React.useState(() => fmt((value as any) ?? (defaultValue as any)));

  // Sync khi value thay đổi từ ngoài (reset form...)
  React.useEffect(() => {
    setDisplay(fmt(value as any));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    const formatted = raw ? formatNumber(Number(raw)) : '';
    setDisplay(formatted);

    // Tạo synthetic event với value là số thuần — react-hook-form register nhận được
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: raw, // số thuần không có dấu phẩy
        name: e.target.name,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
  };

  return (
    <div className="space-y-1.5 w-full">
      {label && (
        <label className="block text-[11px] font-bold uppercase tracking-[0.05em] text-[var(--text-3)] mb-1">
          {label}
        </label>
      )}
      <div className="relative group">
        {icon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] pointer-events-none z-10 transition-colors group-focus-within:text-[var(--brand)]">
            {icon}
          </span>
        )}
        <input
          ref={setRef}
          type="text"
          inputMode="numeric"
          value={display}
          onChange={handleChange}
          onBlur={onBlur}
          className={cn(
            'flex h-[42px] w-full rounded-xl border bg-[var(--bg-1)] px-4 py-2 text-[14px]',
            'text-[var(--text-1)] placeholder:text-[var(--text-3)] shadow-sm',
            'border-[var(--border-c)] outline-none transition-all duration-300',
            'hover:border-[var(--brand)] hover:shadow-md',
            'focus:border-[var(--brand)] focus:ring-4 focus:ring-[var(--brand-dim)] focus:shadow-lg',
            'disabled:cursor-not-allowed disabled:opacity-50',
            icon && 'pl-10',
            error && 'border-destructive focus:border-destructive focus:ring-red-500/10 focus:shadow-none',
            className,
          )}
          {...props}
        />
      </div>
      {error && <p className="text-[11px] font-medium text-destructive mt-1 ml-1 animate-in fade-in slide-in-from-top-1">{error}</p>}
      {hint && !error && <p className="text-[11px] text-[var(--text-3)] mt-1 ml-1">{hint}</p>}
    </div>
  );
});
NumberInput.displayName = 'NumberInput';

export { NumberInput };
